import { memo } from 'react';

interface StructuredDataProps {
  type: 'organization' | 'faq' | 'service' | 'breadcrumb' | 'localbusiness' | 'website';
  data?: any;
}

const StructuredData = memo(({ type, data }: StructuredDataProps) => {
  const getStructuredData = () => {
    switch (type) {
      case 'organization':
        return {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "VERAIS",
          "description": "Automatización inteligente y empleados digitales para PyMEs en Colombia",
          "url": "https://verais.ai",
          "logo": "https://verais.ai/logo.png",
          "image": "https://verais.ai/og-verais.jpg",
          "founder": {
            "@type": "Person",
            "name": "VERAIS Team"
          },
          "foundingDate": "2024",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "CO",
            "addressRegion": "Colombia"
          },
          "contactPoint": [{
            "@type": "ContactPoint",
            "telephone": "+57-320-228-3840",
            "contactType": "sales",
            "availableLanguage": "Spanish",
            "areaServed": "CO"
          }],
          "sameAs": [
            "https://linkedin.com/company/verais-ai"
          ],
          "serviceType": [
            "Automatización de Procesos",
            "Inteligencia Artificial",
            "Empleados Digitales",
            "RPA para PyMEs"
          ]
        };

      case 'faq':
        return {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "¿Qué es un empleado digital?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Un empleado digital es un agente de IA que automatiza tareas repetitivas en tu empresa, trabajando 24/7 sin errores ni vacaciones, integrándose perfectamente con tus sistemas existentes."
              }
            },
            {
              "@type": "Question", 
              "name": "¿Cuánto tiempo toma implementar la automatización?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "La implementación estándar toma 30 días. Incluye análisis de procesos, desarrollo personalizado, integración con sistemas existentes y capacitación del equipo."
              }
            },
            {
              "@type": "Question",
              "name": "¿Qué ROI puedo esperar?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Nuestros clientes obtienen un ROI de 3x en los primeros 90 días, con reducción del 67% en costos operativos y 100% de retención de eficiencia."
              }
            },
            {
              "@type": "Question",
              "name": "¿Funciona con mis sistemas actuales?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Sí, nuestros empleados digitales se integran con cualquier sistema: ERP, CRM, bases de datos, aplicaciones web, APIs y herramientas existentes en tu empresa."
              }
            }
          ]
        };

      case 'service':
        return {
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Empleados Digitales para PyMEs",
          "description": "Automatización inteligente de procesos empresariales con IA para pequeñas y medianas empresas",
          "provider": {
            "@type": "Organization",
            "name": "VERAIS"
          },
          "serviceType": "Automatización de Procesos con IA",
          "areaServed": {
            "@type": "Country",
            "name": "Colombia"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Servicios de Automatización",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Automatización de Procesos Administrativos"
                }
              },
              {
                "@type": "Offer", 
                "itemOffered": {
                  "@type": "Service",
                  "name": "Empleados Digitales de Atención al Cliente"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service", 
                  "name": "Automatización de Ventas y Marketing"
                }
              }
            ]
          }
        };

      case 'localbusiness':
        return {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "VERAIS",
          "description": "Empresa líder en automatización inteligente para PyMEs en Colombia",
          "image": "https://verais.ai/og-verais.jpg",
          "telephone": "+57-320-228-3840",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "CO",
            "addressRegion": "Colombia"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "4.7110",
            "longitude": "-74.0721"
          },
          "url": "https://verais.ai",
          "priceRange": "$$",
          "openingHours": "Mo-Fr 08:00-18:00",
          "sameAs": [
            "https://linkedin.com/company/verais-ai"
          ],
          "@id": "https://verais.ai/#localbusiness"
        };

      case 'website':
        return {
          "@context": "https://schema.org",
          "@type": "WebSite",
          "url": "https://verais.ai/",
          "name": "VERAIS",
          "description": "Automatización inteligente y empleados digitales para PyMEs en Colombia",
          "inLanguage": "es-CO",
          "publisher": {
            "@type": "Organization",
            "name": "VERAIS",
            "url": "https://verais.ai"
          }
        };

      case 'breadcrumb':
        return data || {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Inicio",
              "item": "https://verais.ai/"
            }
          ]
        };

      default:
        return null;
    }
  };

  const structuredData = getStructuredData();

  if (!structuredData) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }}
    />
  );
});

StructuredData.displayName = 'StructuredData';

export default StructuredData;